import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';
import 'leaflet/dist/leaflet.css';
import './i18n/config';
import { API_BASE_URL } from './api/apiBaseUrl';

// Sentry VOR dem Render-Mount initialisieren. browserTracingIntegration injiziert
// sentry-trace/baggage auf Requests an die API-Origin → verbindet FE-Span mit BE-Span
// (identische Trace-ID). Nur aktiv, wenn eine DSN gesetzt ist (Dev/Mock ohne DSN: no-op).
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  const apiBaseUrl = API_BASE_URL;
  Sentry.init({
    dsn: sentryDsn,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [apiBaseUrl.replace(/\/$/, '')],
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.0,
    environment: import.meta.env.MODE,
    // Fällt auf die App-Version aus den Release Notes zurück, damit Sentry-Releases
    // denselben Namen tragen wie das, was der Nutzer im "Was ist neu" sieht.
    release: import.meta.env.VITE_SENTRY_RELEASE || __APP_VERSION__,
  });
}

// Global flag um zu tracken ob MSW bereit ist
declare global {
  interface Window {
    mswReady?: boolean;
  }
}

async function enableMocking() {
  // Nur MSW starten wenn im Development-Mode UND API_MODE === 'mock'
  const apiMode = import.meta.env.VITE_API_MODE || 'mock';
  const apiBaseUrl = API_BASE_URL;

  if (process.env.NODE_ENV !== 'development' || apiMode === 'real') {
    console.log('🌐 API Mode: REAL');
    console.log('📡 API Base URL:', apiBaseUrl);
    console.log('ℹ️  MSW is disabled. Using real backend API.');
    window.mswReady = true; // Auch bei real API als bereit markieren
    return;
  }

  console.log('🔧 API Mode: MOCK');
  console.log('📡 MSW will intercept requests');
  const { worker } = await import('./mocks/browser');

  await worker.start({
    onUnhandledRequest: 'warn', // Zeigt Warnungen für nicht gemockte Requests
  });
  
  window.mswReady = true;
  console.log('✅ MSW is ready and active');
}

enableMocking().then(() => {
  createRoot(document.getElementById('root')!).render(
    <Sentry.ErrorBoundary fallback={<p>Ein Fehler ist aufgetreten.</p>}>
      <App />
    </Sentry.ErrorBoundary>
  );
});
